import { useEffect, useRef, useState } from 'react'
import { markAttendanceWorkOpened } from '../dialogStandDown'
import { useModalShell } from './useModalShell'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { CalendarClock, CalendarX, Ruler, SlidersHorizontal, X } from 'lucide-react'
import {
  fetchClassStandings,
  fetchSheet,
  markAttendance,
  NOT_YOURS,
  needsOverride,
  progress,
  reasonLabel,
  removeMakeupVisitor,
  SESSION_ENDED,
  type AttendanceState,
  type RosterStanding,
  type SheetDancer,
} from '../api/attendance'
import { CallOffConfirm } from './CallOffConfirm'
import { CALL_OFF_REASONS, refreshAfterCallingOff } from '../callOff'
import { fetchRoster, type RosterDancer } from '../api/bookings'
import AddToNight from './AddToNight'
import TakeOffTheNight from './TakeOffTheNight'
import { MarkButtons } from './MarkButtons'
import { clearCover } from '../api/cover'
import { ClassBadge } from './StandingBadge'
import { formatTime, meridiem, today } from '../api/calendar'
import RoutineEditor from './RoutineEditor'
import MoveNightPanel, { hasStarted } from './MoveNightPanel'
import NotCalledPanel from './NotCalledPanel'
import { minutesNow } from '../api/dancers'
import { useCan, useSession } from '../useSession'

/**
 * What the override dialog says, per state the tap asked for (D-639).
 *
 * <b>D-484 wrote this requirement down a month before it was needed.</b> Its
 * comment said the dialog hard-coded <i>Present</i> safely only while Here was
 * the one answer a locked row could give, and that <i>"if Late ever arrives on
 * this panel, the requested state has to travel into the dialog and its wording
 * has to follow"</i>. Four states arrived together; this is the wording half.
 *
 * `Unexcused` is absent on purpose - marking a reported-out dancer absent
 * agrees with her family, so it never reaches a dialog at all.
 */
const TURNED_UP: Partial<
  Record<AttendanceState, { heading: (name: string) => string; confirm: string }>
> = {
  Present: { heading: (name) => `Is ${name} here after all?`, confirm: 'Yes, they are here' },
  Late: { heading: (name) => `Did ${name} arrive late?`, confirm: 'Yes, they came late' },
  Watched: { heading: (name) => `Did ${name} come and watch?`, confirm: 'Yes, they watched' },
}

/**
 * Who is in this session.
 *
 * Opened by clicking a block on the calendar. Deliberately read-only and small:
 * the request was "a quick and easy way to see who is in each class", so it
 * answers that and gets out of the way rather than becoming a class editor.
 */
export default function RosterModal({
  kind,
  id,
  onClose,
  onOpenSession,
  onEditBooking,
}: {
  kind: 'class' | 'booking' | 'classList'
  id: string
  onClose: () => void
  /** Switch to another dancer's session of the same private lesson (D-733). */
  onOpenSession?: (occurrenceId: string) => void
  /** Offered on a private lesson to somebody who may book rooms (D-733). */
  onEditBooking?: (bookingId: string) => void
}) {
  // Attendance work is open: the app's own announcements wait for the next load (D-731).
  useEffect(() => markAttendanceWorkOpened(), [])

  const canEditRoutine = useCan('EditClassRoutine')
  /*
   * Kiosk-safe by omission rather than by luck: a wall tablet holds no
   * RecordMeasurements, so this never renders there and the route refuses it
   * anyway (D-233).
   */
  const mayMeasure = useCan('RecordMeasurements')

  // What `/admin/classes` itself asks for, so the link cannot refuse its
  // holder (D-333, D-460).
  const mayManageClasses = useCan('ManageClasses')

  const [moving, setMoving] = useState(false)

  /*
   * **The register says it happened, because nothing else here would** (D-890).
   *
   * The sheet's own *"This session was cancelled"* line is gated on `marking`,
   * which needs the date to have arrived — and this control only ever acts on a
   * night that has not started. So without this she confirms, the button
   * vanishes, and the screen says nothing at all, at the one moment she most
   * needs telling that sixty families were emailed (D-209).
   */
  const [calledOff, setCalledOff] = useState(false)

  /*
   * Whether a name may be a link to the dancer's record (D-397).
   *
   * **`ViewPeople` is what the destination requires**, so asking it here is
   * asking the same question the route asks — a link that refuses the person
   * holding it is worse than no link.
   *
   * **And never on a tablet**, which is the part that is not obvious:
   * `ViewPeople` *is* in the kiosk ceiling, because a register is a list of
   * children's names (D-233). The record behind this link is not marked
   * `.AllowKiosk()` and would be refused — so the capability alone would offer
   * a wall tablet a door into the dancer directory, which is the precise gap
   * D-233 exists to close.
   */
  const { session } = useSession()
  const canOpenRecord = useCan('ViewPeople') && session?.isKiosk !== true

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['roster', kind, id],
    queryFn: () => fetchRoster(kind, id),
  })

  /*
   * Loaded after the roster, and only for a class — a booking is a reservation,
   * not a lesson, so there is no attendance to report on (D-80).
   *
   * Kept off the roster payload deliberately: this reads every occurrence of
   * every class each of these dancers takes, and "who is in this session?" must
   * still answer instantly when a teacher opens it between classes (D-169).
   */
  const { data: standings } = useQuery({
    queryKey: ['class-standings', data?.studioClassId],
    queryFn: () => fetchClassStandings(data!.studioClassId!),
    enabled: Boolean(data?.studioClassId),
  })

  const standingFor = new Map(standings?.map((s) => [s.dancerId, s]) ?? [])

  /*
   * <b>Whether this panel may take attendance</b> (D-483).
   *
   * Four conditions, and each is a different reason, so the screen can say
   * which one is in the way rather than silently showing nothing:
   *
   * - the capability. No role default withholds it, so this is the account
   *   D-5 narrowed by hand — and the endpoint refuses her too, because a
   *   hidden control with no matching refusal is half a boundary;
   * - a **night**. The class door answers "who is in this class" and has no
   *   date behind it (A-1), and a booking has no class at all (D-80). Keyed
   *   on the payload's content, never on which door opened the panel, so
   *   D-466 stays closed;
   * - the night having arrived. Marking a future session is allowed by
   *   D-189 and `/my-attendance` depends on it — but offering a register for
   *   a class nobody has taught yet is a poor screen, so this is a client
   *   rule with no matching refusal, deliberately (D-482);
   * - the session not being cancelled, which the sheet itself reports.
   */
  const mayTakeAttendance = useCan('TakeAttendance')
  const hasBegun = Boolean(data?.date) && data!.date <= today()
  const marking = mayTakeAttendance && Boolean(data?.occurrenceId) && hasBegun

  /*
   * ⚠ <b>The sheet is also read for a night that has not arrived</b> (D-846).
   *
   * <b>Marking a future session is still refused</b> — that is `marking` above, and
   * D-482's reasoning is untouched: offering a register for a class nobody has taught
   * is a poor screen. But the sheet carries more than marks now. It carries
   * `canDeclareCall` and the sub-team a call would cover, and **declaring a call is
   * only useful in advance**: taking next Wednesday off a family's calendar and
   * telling her she is not needed are both about a rehearsal that has not happened.
   *
   * <b>Without this the whole of slice 2 was reachable only by a hand-made API
   * call</b> — the calendar exclusion shipped, tested and audited, with no way for a
   * teacher to cause it. An audit found it, and it is the reopen-button shape rule 2
   * exists for, one slice later.
   */
  const reading = mayTakeAttendance && Boolean(data?.occurrenceId)

  /*
   * A third query, like the standings above and for the same reason: the
   * roster is `ViewPeople` and carries no marks.
   *
   * ⚠ <b>It is scoped, and this comment said the opposite for a year.</b> It
   * read <i>"a signed-in teacher may read and mark any class's sheet, which is
   * deliberate under D-121"</i> — true when it was written and false since
   * **D-538**, which scoped signed-in callers after an audit marked a register
   * on a class the account only assisted. `KioskMayReachAsync` now answers
   * `TeachingScope.ForAsync(...).Allows(occurrence)` for everybody, kiosk or
   * not. So **a failure here can be the boundary doing its job**, which is the
   * opposite of what the old sentence told a reader to conclude.
   *
   * What survives of D-121 is narrower and still load-bearing: the scope
   * includes a night she is <i>covering</i>, so the person taking a register is
   * frequently not the listed instructor. ⚠ It does <b>not</b> include a class
   * she only assists — `ForAsync` defaults `assisting: false`, which is the
   * whole point of D-538 — and an earlier draft of this correction said it did,
   * which is a wrong fact arriving inside a correction for a wrong fact.
   *
   * That difference is why declaring a sub-team not called asks its own question
   * (D-855): `canDeclareCall`, answered by the server on this sheet.
   * `TeachingScope.MayDeclareCallAsync` lists who is refused it.
   */
  const {
    data: sheet,
    isError: sheetFailed,
    error: sheetError,
  } = useQuery({
    /*
     * <b>The key the other three surfaces already use.</b> This was
     * `['attendance-sheet', id]`, a second cached copy of one server
     * resource that never invalidated the first — so a mark taken here left
     * `/registers` and `/my-attendance` reading a pre-mark register for
     * thirty seconds, and `AdminCoverView`'s `['sheet']` invalidation could
     * not reach this panel at all.
     */
    queryKey: ['sheet', data?.occurrenceId],
    queryFn: () => fetchSheet(data!.occurrenceId!),
    enabled: reading,
  })

  /*
   * Keyed rather than positional: the roster and the sheet are two queries
   * and either can be a moment stale, so pairing them by index would put one
   * dancer's mark on another dancer's row. Both lists go into one map because
   * a dancer is in exactly one of them.
   */
  const markFor = new Map(
    [...(sheet?.enrolled ?? []), ...(sheet?.makeupVisitors ?? [])].map((d) => [d.dancerId, d]),
  )

  const canMarkNow = marking && Boolean(sheet) && sheet?.isCancelled !== true

  /*
   * <b>Whether this row is locked, when the row may not have a mark at all</b>
   * (D-808).
   *
   * <b>The two lists come from two queries and can disagree for a moment.</b>
   * `visitors` is the roster's (`['roster', ...]`); `markFor` is the sheet's
   * (`['sheet', ...]`). Both were read through `needsOverride(markFor.get(id)!)`
   * - and that `!` is a lie: `needsOverride` dereferences `reportedOut`, so a
   * missing mark is not a falsy answer, it is a `TypeError` that takes the
   * whole tree down.
   *
   * <b>Taking a visitor off the night made it reachable</b> (D-806). Both
   * queries are invalidated, the sheet answers first, and for one render the
   * roster still lists a dancer the sheet no longer has - so the register went
   * <b>white</b> and needed a refresh. `observed`, reported from QA within an
   * hour of it shipping.
   *
   * It was unsafe before that and nothing had reached it; a mark arriving late
   * on first open would do the same thing.
   */
  const lockedFor = (dancerId: string) => {
    const mark = markFor.get(dancerId)
    return Boolean(canMarkNow && mark && !unlocked.has(dancerId) && needsOverride(mark))
  }

  /*
   * Null when there is nobody to mark, not just when she may not mark
   * (D-487). An empty class rendered <i>"0 of 0 marked"</i> over <i>"No
   * dancers are enrolled yet"</i>, with a foot exit reading <i>"0 still to
   * mark — your taps are already saved"</i>: two false sentences at once, on
   * a screen a teacher reads on a class night. Reachable for a class the app
   * created, which nothing else can enrol into (D-207).
   */
  const sheetCounts = canMarkNow && sheet ? progress(sheet) : null
  const counts = sheetCounts && sheetCounts.total > 0 ? sheetCounts : null

  const visitors = data?.makeupVisitors ?? []

  /*
   * The headline count and the capacity figure include the visitors (D-480).
   *
   * A room holds what a room holds, and a class taking a visitor on top of a
   * full roster genuinely has fourteen children in it — so `14/13` in red is
   * the true statement, not an off-by-one (D-294). They are counted together
   * and listed apart, which is the same thing the kiosk does.
   */
  const total = (data?.dancers.length ?? 0) + visitors.length

  /*
   * <b>Marks are serialised per dancer, and the next state comes from the
   * last confirmed one</b> (D-483).
   *
   * Two quick taps could otherwise leave a dancer stored in a state nobody
   * chose: the second tap reads the optimistic render rather than what the
   * server has, and the two requests land in whichever order the network
   * decides. The queue is keyed by dancer so a teacher sweeping down a roster
   * is never blocked by somebody else's request.
   */
  const queues = useRef(new Map<string, Promise<unknown>>())
  const [pending, setPending] = useState<Record<string, AttendanceState>>({})

  /*
   * <b>A tap that did not land says so</b> (D-186, D-234).
   *
   * A failure that only rolls back is a green flash the teacher has already
   * looked away from — she finishes the register believing it saved, and
   * attendance is the only data here Jackrabbit cannot regenerate.
   */
  const [failed, setFailed] = useState<string | null>(null)

  /*
   * <b>The dancer whose family reported her out, and who a teacher has just
   * tapped anyway</b> (D-188, Q2).
   *
   * Two steps on purpose, so no accidental tap overrides a parent's report.
   * The kiosk makes this a full-width button under the row; a one-chip row
   * has nowhere to put one, so it is a small dialog that names what the
   * family said.
   */
  /*
   * The dancer and <b>the state the tap asked for</b> (D-639). It carried only
   * the dancer while Here was the one answer a locked row could give; the panel
   * offers four now, and D-484 wrote down in advance what that would break.
   */
  const [confirming, setConfirming] = useState<{ dancer: SheetDancer; state: AttendanceState } | null>(null)

  /*
   * <b>Taking a substitute back off</b> (D-492).
   *
   * Here because this is where she already reads <i>"Hannah Jones (sub)"</i> —
   * she opens the night, sees a name she does not recognise on a class, and
   * the correction is in the same place as the claim. A tested endpoint with
   * nothing pointing at it is the half of the both-directions invariant nobody
   * reports.
   *
   * <b>Bound to `ManageClasses`</b>, which is what the endpoint requires, so
   * the control cannot refuse the person holding it. Correcting who was paid
   * for a night is the studio's, not the teacher's.
   */
  const [clearingCover, setClearingCover] = useState(false)
  const [coverProblem, setCoverProblem] = useState<string | null>(null)

  /*
   * <b>Who she has already confirmed is here</b> — a latch, not a re-derived
   * condition (D-188, and the shape `ClassroomView` already uses).
   *
   * Without it the lock re-arms off the sheet, which is still `Unmarked`
   * until the round trip and the refetch land: she confirms <i>yes, she is
   * here</i>, sees <i>Here</i>, realises it was a mistap and taps to correct
   * — and the dialog reopens and its only affirmative sends <b>Present
   * again</b>. The correction is unavailable and the dancer keeps a mark she
   * did not earn, on the number that gates competing.
   *
   * Cleared when the panel closes, because the question is "has somebody
   * looked at this row just now", not a stored fact.
   */
  const [unlocked, setUnlocked] = useState<Set<string>>(new Set())

  const queryClient = useQueryClient()

  /*
   * <b>Taking a visitor back off the night</b> (D-806).
   *
   * <b>The roster and the sheet both change</b>, and they are separate reads:
   * the row disappears from one and the marks from the other. Invalidating
   * only the sheet would leave her name on the register with nothing to tap.
   *
   * <b>And the absences</b>, because removing her hands the missed class back
   * to needing a make-up — so the picker that offered it must stop believing
   * it is spoken for.
   */
  const takeOff = useMutation({
    mutationFn: (dancerId: string) => removeMakeupVisitor(data!.occurrenceId!, dancerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sheet'] })
      void queryClient.invalidateQueries({ queryKey: ['roster'] })
      void queryClient.invalidateQueries({ queryKey: ['absences'] })
    },
  })

  /*
   * Every tap goes through here, so the lock cannot be bypassed by a second
   * caller later — one function answers "may this tap land", and the confirm
   * is the only thing that clears it.
   */
  function tap(dancer: SheetDancer, state: AttendanceState, note?: string, familyNote?: string) {
    /*
     * <b>Absent agrees with the family, so it asks nothing</b> (D-639). The
     * dialog exists to stop a reported-out dancer being recorded as having
     * turned up when she did not; marking her absent is the report being
     * confirmed, and a confirmation step there is a question with one sensible
     * answer. It only became reachable when the panel grew past two states.
     */
    const contradictsTheReport = state !== 'Unexcused'

    if (contradictsTheReport && needsOverride(dancer) && !unlocked.has(dancer.dancerId)) {
      // The state travels with her: the panel offers four now, and a dialog
      // that assumed Here would turn a Watched into an attendance (D-639).
      setConfirming({ dancer, state })
      return
    }

    mark(dancer.dancerId, state, dancer.name, note, familyNote)
  }

  function mark(dancerId: string, state: AttendanceState, name: string, note?: string, familyNote?: string) {
    if (!data?.occurrenceId) return

    setPending((p) => ({ ...p, [dancerId]: state }))

    const previous = queues.current.get(dancerId) ?? Promise.resolve()

    /*
     * <b>Only the last tap clears the optimistic state</b> (D-483).
     *
     * Tap two chains onto tap one's whole chain, so tap one's `finally` runs
     * <i>before</i> tap two's request is even issued. Deleting the key
     * outright meant the chip fell back to the sheet for the length of the
     * second round trip: it read Absent, reverted to Here, and a third tap
     * during that window cycled from the wrong state and stored the opposite
     * of what three taps mean.
     *
     * The queue map is the identity. If a later tap has replaced this entry,
     * that tap owns the optimistic state and this one leaves it alone.
     */
    let next: Promise<unknown>

    next = previous
      .catch(() => {})
      .then(async () => {
        const result = await markAttendance(data.occurrenceId!, [{ dancerId, state, note, familyNote }])

        /*
         * <b>Reported, never swallowed.</b> A stale panel left open after she
         * dropped names a dancer this session does not have; the server
         * reports her rather than inventing a record, and a screen that read
         * only the HTTP status would call that success.
         */
        if (result.ignored.includes(dancerId)) throw new Error('not on this session')

        await queryClient.invalidateQueries({ queryKey: ['sheet', data.occurrenceId] })

        /*
         * Cleared by a mark that <b>worked</b>, never by the next tap.
         *
         * Clearing on tap looks equivalent and is not: sweeping twelve
         * dancers, a failure on the third vanishes the moment she taps the
         * fourth - about a second later - and she finishes the register
         * believing it saved. That is D-186's failure with an extra step.
         * The kiosk clears on success for the same reason.
         */
        setFailed(null)
      })
      .catch((e: unknown) => {
        /*
         * <b>A dead session is not a failed request</b> (D-234).
         *
         * Telling somebody whose session has ended to tap again instructs her
         * to repeat the one action now guaranteed to fail, and she loses
         * every mark after it. The kiosk had to learn this twice. Reachable
         * here through `InvalidateSessions()` - any capability change to a
         * teacher mid-register signs her out on her next tap (D-72, D-73).
         */
        const why = e instanceof Error ? e.message : ''

        setFailed(
          why === SESSION_ENDED
            ? `${name} did not save - you have been signed out. Sign in again, and the marks you already made are safe.`
            : `${name} did not save. Tap it again.`,
        )
      })
      .finally(() => {
        if (queues.current.get(dancerId) !== next) return

        setPending((p) => {
          const { [dancerId]: _gone, ...rest } = p
          return rest
        })
      })

    queues.current.set(dancerId, next)
  }

  /*
   * <b>Escape, the scroll lock and the focus trap are the shared shell now</b>
   * (D-515).
   *
   * This wrote its own version of all three. The rule it enforced by hand — the
   * confirm absorbs Escape, so backing out of a confirmation does not also
   * close the register behind it — is what the shell gives every dialog by
   * letting only the topmost one answer, which is why the confirm gets its own
   * call below rather than a special case here.
   *
   * The grid behind scrolls horizontally, so the lock still matters: letting it
   * move under the dialog loses your place in a wide schedule.
   */
  const panel = useRef<HTMLDivElement>(null)
  const pressedBackdrop = useRef(false)

  useModalShell({ panel, onDismiss: onClose })

  /*
   * The confirmation is markup *inside* the panel rather than a dialog beside
   * it, so it needs the shell's stack for the ordering and cannot get it by
   * being a separate component. `active` is how a dialog behind its owner's own
   * flag joins that stack — the hook cannot be called conditionally.
   */
  const confirmPanel = useRef<HTMLDivElement>(null)

  useModalShell({
    panel: confirmPanel,
    onDismiss: () => setConfirming(null),
    active: confirming !== null,
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-5"
      /*
        The press is remembered as well as the click (D-515). A click is
        dispatched at the nearest common ancestor of its press and release, so
        selecting a dancer's name and overshooting onto the dim area retargets
        the click to the backdrop — and closing on that discards a register
        halfway through being taken.
      */
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdrop.current) onClose()
      }}
    >
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Class roster"
        // Stops a click inside from reaching the backdrop's close handler.
        onClick={(e) => e.stopPropagation()}
        /*
          Wider whenever there is a class behind it, because that is when the
          routine form is on screen - 26rem is right for a list of names and
          cramped for a form (D-319). It followed the *origin* before, so the
          same class was 32rem from one door and 26rem from another (D-466). A
          booking has no class and keeps the narrower measure.
        */
        /*
          <b>A flex column, so the body gets what is left rather than a
          guess</b> (D-487).

          The body was `max-h-[calc(80vh-5.5rem)]`, which assumes an 88px
          header. Real Jackrabbit names wrap: `observed` at 390px, the header
          renders <b>157px</b> and the body overhangs a parent with
          `overflow-hidden` by 70px. Harmless while the overhang only clipped
          the last row's padding; not once the primary exit lives at the foot
          — measured 18px past the clip, with its caption 44px past and
          entirely invisible, and unreachable because the scroll range ends
          inside the clipped region.

          Small rosters fit, which is why it survives a casual look.
        */
        className={`flex max-h-[80vh] w-full flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl ${
          data?.studioClassId ? "max-w-[32rem]" : "max-w-[26rem]"
        }`}
      >
        <header
          className="room-tint flex shrink-0 items-start gap-3 border-b border-line px-5 py-4"
          style={
            {
              '--room-color': data?.roomColor ?? 'var(--color-ink-3)',
              borderLeft: '4px solid var(--room-display)',
            } as React.CSSProperties
          }
        >
          <div className="min-w-0 flex-1">
            {/*
              Wrapped, not truncated. This modal is opened to find out *which*
              class it is, and the distinguishing part of a Jackrabbit name is
              at the end — `… | TU 400 | 9/10 | SMALL | COMP | MADDIE`. Clipping
              it removes the answer. The modal can grow; the header cannot.
            */}
            {/*
              **A private lesson is headed as one** (D-742).

              A private's session hangs off the class it makes up (D-289,
              D-729), so `title` is that class's name — and the panel opened
              from a private's card was headed `COMP_TECH_TAP | TU 500 | …`,
              which reads as an ordinary class. The only word saying otherwise
              was the chip three lines below, which is not where anybody looks.
              Reported from QA: *tonya's view is showing it as a normal class
              not a private*.

              **The dancer, because that is what her card said.** The schedule
              draws a private as its booking, labelled with the dancer's name,
              so heading the panel the same way makes the two agree. The class
              is not lost — it moves to the line under the chip, which is where
              "what does this count towards" belongs.
            */}
            <h2 className="font-display text-lg wrap-anywhere">
              {data?.bookingId
                ? ['Private lesson', ...(data.lessonSessions ?? []).map((s) => s.dancerName)].join(' · ')
                : (data?.title ?? 'Loading...')}
            </h2>
            {data && (
              <p className="mt-0.5 text-[12.5px] text-ink-3">
                {data.startTime && (
                  <>
                    {formatTime(data.startTime)}
                    {meridiem(data.startTime)}
                    {data.endTime && (
                      <>
                        &ndash;{formatTime(data.endTime)}
                        {meridiem(data.endTime)}
                      </>
                    )}
                  </>
                )}
                {data.roomName && <> &middot; {data.roomName}</>}
                {data.instructors.length > 0 && <> &middot; {data.instructors.join(', ')}</>}
              </p>
            )}

            {/*
              **A private lesson opened from its one card on a schedule** (D-733).
              The card is the lesson, so this is where its booking is changed,
              and where a duet reaches the other dancer's attendance - each
              dancer's is its own session.
            */}
            {data?.bookingId && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {/*
                  Which class it counts towards, now that the heading says what
                  it is (D-742). It was the word *Private lesson* here, which
                  the heading has taken over — and a teacher standing in front
                  of the dancer needs the class, because that is what the lesson
                  banks or makes up (D-729).
                */}
                <span className="text-[12px] font-semibold text-ink-2 wrap-anywhere">{data.title}</span>
                {(data.lessonSessions ?? []).length > 1 &&
                  onOpenSession &&
                  (data.lessonSessions ?? []).map((s) => (
                    <button
                      key={s.occurrenceId}
                      type="button"
                      aria-pressed={s.occurrenceId === data.occurrenceId}
                      onClick={() => onOpenSession(s.occurrenceId)}
                      className={`min-h-[32px] rounded-full border px-2.5 text-[12px] font-semibold ${
                        s.occurrenceId === data.occurrenceId
                          ? 'border-accent bg-accent-soft text-accent-2'
                          : 'border-line-2 bg-surface text-ink-2'
                      }`}
                    >
                      {s.dancerName}
                    </button>
                  ))}
                {onEditBooking && (
                  <button
                    type="button"
                    onClick={() => onEditBooking(data.bookingId!)}
                    className="min-h-[32px] rounded-full border border-line-2 bg-surface px-2.5 text-[12px] font-semibold text-ink-2 hover:border-ink-3"
                  >
                    Change or cancel booking
                  </button>
                )}
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            className="grid size-7 shrink-0 place-items-center rounded-lg text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isPending && <p className="text-sm text-ink-3">Loading the roster...</p>}

          {isError && (
            <p className="rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[13px] text-crit">
              {(error as Error).message}
            </p>
          )}

          {/*
            <b>Only when there is one, and only for the studio</b> (D-492).
            Keyed on the payload's own flag rather than on the "(sub)" suffix
            in the heading, which is a label written for a person to read.
          */}
          {data?.hasSubstitute && mayManageClasses && (
            <div className="mb-3 rounded-lg border border-line bg-surface-2 px-3 py-2">
              <p className="text-[12.5px] text-ink-2">
                {data.instructors.join(', ')} is recorded as covering this night.
              </p>

              <button
                type="button"
                disabled={clearingCover}
                onClick={async () => {
                  setClearingCover(true)
                  setCoverProblem(null)

                  try {
                    await clearCover(data.occurrenceId!)
                    await queryClient.invalidateQueries({ queryKey: ['roster'] })
                    await queryClient.invalidateQueries({ queryKey: ['calendar'] })
                  } catch (e) {
                    setCoverProblem(e instanceof Error ? e.message : 'That did not save.')
                  } finally {
                    setClearingCover(false)
                  }
                }}
                className="mt-1.5 min-h-[44px] text-[13px] font-semibold text-accent-2 disabled:opacity-60"
              >
                {clearingCover ? 'Removing...' : 'Not covering after all — remove'}
              </button>

              {coverProblem && (
                <p className="mt-1 text-[12.5px] text-crit" role="alert">
                  {coverProblem}
                </p>
              )}
            </div>
          )}

          {/*
            <b>Class-level, so it does not depend on which door you came
            through</b> (D-466). This was gated on `kind === "classList"`, so a
            teacher opening the same class from her schedule got a bare list of
            names and the same teacher opening it from *My classes* got the
            routine and the tape measure. `asked`: *"instead of it being two
            diff modals based on if the origin was schedule or if the origin was
            classes tab."*
          */}
          {data && data.studioClassId && (
            <RoutineEditor
              classId={data.studioClassId}
              routineName={data.routineName ?? null}
              musicUrl={data.musicUrl ?? null}
              choreoLinks={data.choreoLinks ?? []}
              canEdit={canEditRoutine}
            />
          )}

          {/*
            <b>The two ways on from a roster</b>, in one row so they wrap
            together on a phone rather than stacking two full-width buttons
            above the names somebody opened this to read.
          */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {/*
              <b>One night, moved</b> (D-698, D-701, pilot item 24).

              <b>Four conditions, and each is a different reason</b>, the same
              shape the attendance gate above uses:

              - <b>hers</b>. `data.mayMove` is the server's own answer, from
                the same function the move route asks (D-701). It was
                `useCan('MoveOwnSessions')`, which is what kind of thing she
                may do and never which classes — so the control sat on every
                class in the building, because a teacher may open any of their
                rosters from the studio schedule (D-299);
              - a <b>class</b> and a <b>night</b>. The server answers false for
                both the class door, which has no occurrence behind it (A-1),
                and a booking, which has no class at all (D-80) — so these two
                are not a second opinion. <b>They are here because
                `MoveNightPanel` takes them as non-null `string` props</b>, and
                without them TypeScript cannot narrow. Do not delete them as
                redundant; they are load-bearing to the compiler and merely
                agree with the server;
              - the night not having **started**. The server refuses a past
                night outright, and since D-704 tonight's too once its start
                time has passed — the register is being taken and families are
                walking in — so offering it would be a control that always
                fails. It compared dates alone before, which offered tonight's
                class for moving an hour after it had finished;
              - the night not being cancelled, which is a refusal the server
                also makes and the sheet already reports on screen. Only known
                where the sheet loaded, so an un-begun cancelled night still
                reaches the server's own sentence rather than a silent nothing.
            */}
            {data && data.studioClassId && data.occurrenceId && data.mayMove
              && !hasStarted(data.date, data.startTime, today(), minutesNow())
              && sheet?.isCancelled !== true && (
              <button
                type="button"
                onClick={() => setMoving((was) => !was)}
                aria-expanded={moving}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
              >
                <CalendarClock size={14} strokeWidth={2.5} className="shrink-0" />
                Move this night
              </button>
            )}

            {/*
              **Beside the move, because they are the two answers to one
              problem** (D-890). `asked` 2026-10-12: solos, duets and trios are
              called off fairly regularly when a dancer cannot make it, and
              until now the only route to this control was the declare-out
              dialog — which made her say *she* was absent to record that the
              dancer was.

              Gated the same three ways as the move, and for the same reasons:
              the server's own answer (D-888), the night not having started
              (D-704), and the night not being already off. The last is only
              known where the sheet loaded, so an un-begun cancelled night
              still reaches the route's own sentence rather than a silent
              nothing (D-209).
            */}
            {data?.occurrenceId && data.mayCallOff
              && !hasStarted(data.date, data.startTime, today(), minutesNow())
              && sheet?.isCancelled !== true && (
              <CallOffConfirm
                occurrenceId={data.occurrenceId}
                trigger={
                  <>
                    <CalendarX size={14} strokeWidth={2.5} className="shrink-0" />
                    Call this off
                  </>
                }
                triggerClassName="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-crit hover:text-crit"
                reasonLabel="Why? Only the studio sees this."
                reasonPlaceholder="In your own words"
                reasons={CALL_OFF_REASONS}
                onDone={() => {
                  setCalledOff(true)
                  refreshAfterCallingOff(queryClient)
                }}
              />
            )}

            {/*
              **The teacher's way into a measuring sitting** (D-440, D-451).

              The screen shipped with one doorway, on the class panel — which is
              behind `ManageClasses`, so Tonya could reach it and a teacher could
              not. That is the slice built *for* teachers, offered to everybody
              except them: a URL that works while nothing points at it, which is
              the same fault as a link that refuses the person holding it and
              harder to notice, because nobody reports a screen they have never
              been shown.

              Here rather than on the class card behind it, because this dialog is
              already where she stands with the roster open — which is the moment
              the tape is in her other hand.
            */}
            {/*
              <b>The enrolled list, deliberately, not the total</b> (D-480).

              A visitor is measured for her own class by her own teacher; a
              costume is not ordered off a night she dropped into. So this is
              the one count in this panel that did not become `total` when the
              visitors arrived, and it says so rather than looking like one
              that was missed.
            */}
            {data && data.studioClassId && mayMeasure && data.dancers.length > 0 && (
              <Link
                to={`/classes/${data.studioClassId}/measure`}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
              >
                <Ruler size={14} strokeWidth={2.5} className="shrink-0" />
                Measure the class
              </Link>
            )}

            {/*
              <b>The class itself, for the person who can change it</b> (D-465).

              `asked`: *"if they click on their class, and then are like oh
              yeah i needed to update xyz inside the class but there's no link
              to it so they have to go back to classes tab first and then find
              their class."*

              <b>Gated on `ManageClasses`, which is what the destination
              asks.</b> That makes it Tonya's — and it is hers by nature rather
              than by restriction: every class-editing endpoint requires the
              same capability, so the one thing a teacher may change about a
              class is the routine name and music, and `RoutineEditor` is
              already in this dialog. There is nothing further to offer her, and
              a link she cannot follow would be worse than none (D-333).

              <b>Null for a booking</b>, which is a reservation rather than a
              lesson and has no class behind it (D-80).
            */}
            {data && data.studioClassId && mayManageClasses && (
              <Link
                to={`/admin/classes/${data.studioClassId}`}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-line-2 px-3 text-[13px] font-semibold text-ink-2 hover:border-ink-3"
              >
                <SlidersHorizontal size={14} strokeWidth={2.5} className="shrink-0" />
                Class details
              </Link>
            )}
          </div>

          {/*
            Below the row rather than inside it: the form is three fields wide
            and would break a row of buttons that is meant to wrap on a phone.
          */}
          {moving && data?.studioClassId && data.occurrenceId && (
            <MoveNightPanel
              classId={data.studioClassId}
              occurrenceId={data.occurrenceId}
              date={data.date}
              startTime={data.startTime}
              endTime={data.endTime}
              // Which option in the picker is the one it is in (D-893, D-896).
              roomId={data.roomId ?? null}
              // Counted, never read off a flag (D-665, D-898).
              dancerIds={(data.dancers ?? []).map((d) => d.dancerId)}
              teachingStaffIds={data.teachingStaffIds ?? []}
              onDone={() => setMoving(false)}
            />
          )}

          {data && (
            <>
              {/*
                <b>A cancelled session cannot be marked, and says which</b>
                (2.13). Without the sentence this is a roster with no chips,
                which is the same thing a teacher sees when she opens somebody
                else's class &mdash; two different situations rendering
                identically is exactly what D-209 is about.
              */}
              {/*
                **Her own confirmation, and it is a different sentence from the
                one below** (D-890).

                That one is gated on `marking`, which needs the date to have
                arrived — and this control only ever acts on a night that has
                *not* started, so it can never be what she reads. Without this
                she confirms, the button disappears and the screen says nothing,
                at the moment she most needs telling that the families were
                emailed (D-209). It also says the families were told rather than
                that they *will* be: the fan-out is handed off and the bell
                follows (D-869).
              */}
              {calledOff && (
                <p className="mb-3 rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2">
                  {/*
                    ⚠ **It does not claim anybody was told**, and the first
                    draft did. The route answers 200 with `telling: 0` for a
                    class nobody is enrolled in — reachable, because a class the
                    app made has no enrolment path (D-207) — so *"the families
                    are being told"* would be the D-209 shape this sentence was
                    added to avoid, one case over. The confirm already said how
                    many would hear; this says the deed is done and the bell
                    carries the rest (D-869).
                  */}
                  This session is called off. Put it back on from your own day if you need to.
                </p>
              )}

              {marking && sheet?.isCancelled && (
                <p className="mb-3 rounded-lg border border-warn bg-warn-bg px-3 py-2 text-[12.5px] text-warn">
                  This session was cancelled, so there is no attendance to take.
                </p>
              )}

              {/*
                <b>Whether a sub-team was called tonight</b> (D-855). Above the
                rows, because it is the reason two thirds of them may read
                <i>Not called</i> — read after the fact, a register full of a
                state nobody set looks like a fault.
              */}
              {sheet && (
                <NotCalledPanel sheet={sheet} />
              )}

              {/*
                <b>The roster arrived and attendance did not</b>, which is the
                normal shape of a partial failure here rather than the rare one
                &mdash; two requests, and the client gives up on one after
                about a second (D-209). The sentence names <i>which</i>, so a
                missing lookup never renders as a statement about the data.
              */}
              {reading && sheetFailed && (
                <p className="mb-3 rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[12.5px] text-crit">
                  {(sheetError as Error)?.message === SESSION_ENDED
                    ? 'You have been signed out, so attendance could not load - and nobody has been marked absent. Sign in again.'
                    /*
                      <b>A refusal is not a failure, and the remedy is opposite</b>
                      (D-209, D-538). This branch is new: the sheet is scoped for
                      signed-in callers too, so a teacher opening a class that is
                      not hers was told to <i>try again in a moment</i> — advice
                      that could never work, against a boundary behaving correctly.
                      `fetchSheet` already told the two apart; only the sentence was
                      missing.
                    */
                    : (sheetError as Error)?.message === NOT_YOURS
                      ? 'This class is not one of yours, so its attendance cannot be opened here - and nobody has been marked absent.'
                      : 'The roster loaded, but attendance did not, so nothing can be marked here yet - and nobody has been marked absent. Try again in a moment.'}
                </p>
              )}

              {failed && (
                <p
                  role="alert"
                  className="mb-3 rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[12.5px] text-crit"
                >
                  {failed}
                </p>
              )}

              <div className="mb-3 flex items-baseline justify-between">
                <span className="text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
                  {/*
                    <b>How far through the register she is</b> (D-269, D-487).

                    Carried out of `TeacherRegister` before it was deleted. A
                    teacher between two back-to-back classes has about fifteen
                    seconds; "1 of 3 marked" is what she needs, and counting
                    twelve chips is not.
                  */}
                  {counts ? `${counts.marked} of ${counts.total} marked` : null}
                  {!counts && `${total} ${total === 1 ? 'dancer' : 'dancers'}`}
                </span>
                {data.capacity !== null && (
                  <span
                    className={`tabular text-[12px] font-semibold ${
                      total >= data.capacity ? 'text-crit' : 'text-ink-3'
                    }`}
                  >
                    {total}/{data.capacity}
                  </span>
                )}
              </div>

              {total === 0 ? (
                <p className="py-6 text-center text-[13px] text-ink-3">
                  {data.kind === 'booking'
                    ? 'Nobody is booked into this slot yet.'
                    : 'No dancers are enrolled yet.'}
                </p>
              ) : (
                <>
                  <ul className="flex flex-col divide-y divide-line">
                    {data.dancers.map((dancer) => (
                      <DancerRow
                        key={dancer.dancerId}
                        dancer={dancer}
                        standing={standingFor.get(dancer.dancerId)}
                        canOpenRecord={canOpenRecord}
                        onClose={onClose}
                        mark={canMarkNow ? markFor.get(dancer.dancerId) : undefined}
                        marking={(state, note, familyNote) =>
                          tap(markFor.get(dancer.dancerId)!, state, note, familyNote)
                        }
                        busy={dancer.dancerId in pending}
                        optimistic={pending[dancer.dancerId]}
                        locked={lockedFor(dancer.dancerId)}
                      />
                    ))}
                  </ul>

                  {/*
                    <b>Her own section, never merged into the roster</b>
                    (PRD &sect;7.6, D-480).

                    A teacher needs to know which dancers are hers. The first
                    cut of this concatenated the two lists and marked the
                    visitor with a word in a right-hand column, which put an
                    unfamiliar name in the middle of an alphabetical list of
                    her own dancers &mdash; the one thing three separate
                    comments in this codebase say not to do. The kiosk taking
                    the same attendance has always rendered this heading.
                  */}
                  {visitors.length > 0 && (
                    <>
                      <h3 className="mt-5 mb-1 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
                        Making up
                      </h3>
                      <ul className="flex flex-col divide-y divide-line">
                        {visitors.map((dancer) => (
                          <DancerRow
                            key={dancer.dancerId}
                            dancer={dancer}
                            visiting
                            canOpenRecord={canOpenRecord}
                            onClose={onClose}
                            mark={canMarkNow ? markFor.get(dancer.dancerId) : undefined}
                            marking={(state, note, familyNote) =>
                          tap(markFor.get(dancer.dancerId)!, state, note, familyNote)
                        }
                            busy={dancer.dancerId in pending}
                            optimistic={pending[dancer.dancerId]}
                            locked={lockedFor(dancer.dancerId)}
                            /*
                              Offered only where she could add one in the first
                              place (D-806) - the same permission, so a panel
                              that is read-only stays read-only.
                            */
                            onRemove={canMarkNow ? () => takeOff.mutate(dancer.dancerId) : undefined}
                          />
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}

              {/*
                <b>Adding a dancer to this one night</b> (D-632).

                The wall tablet has had this since makeups arrived; a teacher
                signed in on her own phone had no way to do it, and the pilot
                asked for both. Same endpoint, same result: a visitor row on
                this occurrence, which is a banked credit when she is marked
                present and no absence was named.

                <b>Under the same four conditions as marking</b>, because it
                is the same act: adding somebody to a night she may not take a
                register for would be a row nobody could then mark. Capacity
                is deliberately not a refusal - the count above already turns
                red and says <i>14/13</i> (D-294, D-480), and sending a dancer
                home from the door is not this screen's job.
              */}
              {canMarkNow && data.occurrenceId && (
                <AddToNight
                  occurrenceId={data.occurrenceId}
                  onAdded={() => {
                    void queryClient.invalidateQueries({ queryKey: ['sheet', data.occurrenceId] })
                    void queryClient.invalidateQueries({ queryKey: ['roster', kind, id] })
                  }}
                />
              )}

              {/*
                <b>The way out, at the end</b> (D-269, D-487).

                Carried out of `TeacherRegister`, which learned it on a phone:
                the only exit was at the top, so finishing a register meant
                scrolling the whole roster again to leave &mdash; past twenty
                dancers, holding an iPad in the other hand. The last thing she
                does is mark the last dancer, so the exit belongs where that
                leaves her.

                <b>Present whether or not she finished.</b> A teacher
                interrupted halfway needs to leave more urgently than one who
                is done, and every tap has already saved &mdash; there is no
                submit (D-116). What changes is only what the button says.
              */}
              {counts && (
                <div className="mt-6">
                  {counts.done && (
                    <p className="mb-2.5 text-center text-[14px] font-semibold text-good">
                      Everyone marked
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={onClose}
                    className={`min-h-[44px] w-full rounded-xl py-3 text-[15px] font-semibold ${
                      counts.done
                        ? 'bg-accent text-on-accent'
                        : 'border border-line bg-surface text-ink-2'
                    }`}
                  >
                    {counts.done ? 'Done' : 'Back to today'}
                  </button>

                  {!counts.done && (
                    <p className="mt-2 text-center text-[12px] text-ink-3">
                      {counts.total - counts.marked} still to mark &mdash; your taps are already
                      saved.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/*
        <b>What her family actually said</b> (Q2, D-9).

        The reason and who reported it, never the parent's own note — that can
        carry anything a parent chose to type, and this is a confirmation on a
        teacher's phone. The note stays where the capability for it lives.
      */}
      {confirming && (
        <div
          /*
             <b>An alert inside the panel, not a second modal beside it.</b>
             It was a sibling of the panel's own dialog with its own
             `aria-modal`, so two modals were live at once and an assistive
             technology honouring the outer one should not have exposed this
             at all. `alertdialog` says what it is; the heading names her, so
             `aria-labelledby` carries the dancer rather than a generic line.
          */
          ref={confirmPanel}
          tabIndex={-1}
          role="alertdialog"
          aria-labelledby="confirm-heading"
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-x-5 top-1/2 mx-auto w-full max-w-[22rem] -translate-y-1/2 rounded-2xl border border-line bg-surface p-5 shadow-2xl"
        >
          <h3 id="confirm-heading" className="font-display text-lg">
            {TURNED_UP[confirming.state]?.heading(confirming.dancer.name) ??
              `Is ${confirming.dancer.name} here after all?`}
          </h3>

          <p className="mt-1.5 text-[13px] text-ink-3">
            {confirming.dancer.reportedBy ?? 'Her family'} told the studio she would miss this
            {reasonLabel(confirming.dancer.reason) ? ` — ${reasonLabel(confirming.dancer.reason)}` : ''}.
          </p>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => {
                /*
                 * `Present` rather than the state the tap asked for, and
                 * they are the same thing: `needsOverride` is only true for
                 * Unmarked, Unexcused and Excused, and `nextMark` maps all
                 * three to Present. The dialog's own words — <i>is she here
                 * after all?</i> — only make sense for that one answer.
                 *
                 * <b>This depends on Late not being markable here</b>
                 * (D-484). A draft added a long press and a picker, and the
                 * moment it did, a reported-out dancer who turned up
                 * <i>late</i> was silently stored as on time — the comment
                 * above was written and falsified in the same change. If Late
                 * ever arrives on this panel, the requested state has to
                 * travel into the dialog and its wording has to follow.
                 */
                mark(confirming.dancer.dancerId, confirming.state, confirming.dancer.name)
                setUnlocked((u) => new Set(u).add(confirming.dancer.dancerId))
                setConfirming(null)
              }}
              className="min-h-[44px] flex-1 rounded-lg bg-accent px-3 py-2 text-[13px] font-bold text-on-accent"
            >
              {TURNED_UP[confirming.state]?.confirm ?? 'Yes, they are here'}
            </button>

            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="min-h-[44px] flex-1 rounded-lg border border-line px-3 py-2 text-[13px] font-semibold"
            >
              Keep her marked out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * One dancer on a roster.
 *
 * <b>One composition, two callers</b> &mdash; the enrolled list and the
 * visitors below it. Rebuilding the row from the same pieces is how two
 * copies of one idea drift, and the second copy never receives the first's
 * fixes.
 *
 * <b>`visiting` comes from the section, not from the row.</b> A visitor is
 * one because of the list she arrived in; deriving it from `makesUpFor`
 * would make a visitor whose missed session is unknown render as enrolled.
 */
function DancerRow({
  dancer,
  standing,
  visiting = false,
  canOpenRecord,
  onClose,
  mark,
  marking,
  busy = false,
  optimistic,
  locked = false,
  onRemove,
}: {
  dancer: RosterDancer
  standing?: RosterStanding
  visiting?: boolean
  /**
   * Takes a visiting dancer back off this night (D-806). Absent means the row
   * cannot be undone from here — a read-only panel, or an enrolled dancer,
   * who the server refuses anyway.
   */
  onRemove?: () => void
  canOpenRecord: boolean
  onClose: () => void
  /**
   * Her row on the attendance sheet, when this panel may mark. Absent means
   * the panel is read-only, which is four different situations and each is
   * explained where `marking` is computed.
   */
  mark?: SheetDancer
  marking?: (state: AttendanceState, note?: string, familyNote?: string) => void
  busy?: boolean
  /**
   * The state a tap has asked for and the server has not confirmed.
   *
   * <b>The chip paints it immediately</b>, and the next tap cycles from it —
   * which is the whole reason it exists. A teacher sweeping a roster taps
   * twice to mean <i>Absent</i>, and a chip that waited for the round trip
   * would compute the second tap from <i>Unmarked</i> and land on
   * <i>Here</i>: two taps producing the opposite of what two taps mean.
   *
   * The hazard this does <b>not</b> reintroduce is requests landing out of
   * order — that is what serialising per dancer is for. If the request fails,
   * this clears and the chip falls back to the sheet, so the screen never
   * keeps a state the server refused.
   */
  optimistic?: AttendanceState
  /** Her family reported her out and a tap will ask before marking (D-188). */
  locked?: boolean
}) {
return (
  <li className="py-2">
  {/*
    <b>A column now, because marking is four buttons and a note</b> (D-639).
    It was one chip in the standing column, which is what a two-state cycle
    fits into; the four states a teacher actually has do not, and squeezing
    them into a row on a 390px phone is what produced the cycle in the first
    place. The standing badge gets its column back as a result.
  */}
  <div className="flex items-baseline gap-2">
    <div className="min-w-0 flex-1">
      {/*
        **The name opens her record** (D-397).

        D-397 let a teacher record sizes for a dancer she
        teaches, and a cold audit found she had no way to
        reach the screen: People and Dancers are both behind
        `ManagePeople`, and the only other thing in the app
        that emits this address is an attendance notification.
        The permission was right and the door did not exist —
        the "a route nothing offers" half of the invariant.

        **Here rather than in the nav**, because this is where
        she is standing: a roster is the list of children in
        front of her, and it is the one screen that already
        knows which dancers are hers.

        **It cannot refuse the person holding it.** The record
        is `RequireCapability="ViewPeople"`, which is what a
        teacher already needs to see this roster at all
        (D-233's ceiling includes it), so anybody who can read
        this line can open the destination.
      */}
      {canOpenRecord ? (
        <Link
          to={`/admin/dancers/${dancer.dancerId}`}
          onClick={onClose}
          /*
            `py-0.5` is a tap target, not spacing (D-481). At 13.5px the line
            box is 20px, and this is the link a teacher taps on her phone
            while standing in front of the dancer, which is the whole reason
            D-397 put it here. Four pixels take it to 24px. Padding rather
            than a height, so the baseline the row aligns on does not move.
          */
          className="block truncate py-0.5 text-[13.5px] font-semibold text-ink no-underline hover:text-accent-2"
        >
          {dancer.name}
        </Link>
      ) : (
        // Matching padding so a row reads the same height whether or not the
        // name is a link (D-481).
        <span className="block truncate py-0.5 text-[13.5px] font-semibold">{dancer.name}</span>
      )}

      {/*
        <b>Why an unfamiliar dancer is in her room.</b> The kiosk puts the
        same sentence under the name, and it is the reason the visitor is
        worth showing at all &mdash; a name on its own tells a teacher
        nothing she can act on.

        Absent when the sheet does not know which session is being made up,
        which is why `visiting` is a prop rather than derived from this.

        <b>Wrapped, never clipped.</b> The label is the raw class name plus
        the date &mdash; `COMP_TECH_BALLET_2 | TU 400 | 9/10 | SMALL | COMP |
        MADDIE, 12 August` &mdash; and the distinguishing part of a
        Jackrabbit name is at the *end*, with the date after it. Truncating
        leaves the prefix, which is the half that does not identify
        anything. `wrap-anywhere` because an underscore offers CSS no break
        opportunity, so a 25-character token would otherwise widen the row.
      */}
      {visiting && dancer.makesUpFor && (
        <p className="mt-0.5 text-[12px] text-ink-3 wrap-anywhere">
          Making up {dancer.makesUpFor}
        </p>
      )}

      {/*
        <b>The undo for a wrongly-tapped drop-in</b> (D-806).
        <b>Only on a visitor</b>, which is also the server's rule - a dancer
        enrolled in this class is refused there, and offering the control here
        would be a button whose job is to fail.
      */}
      {visiting && onRemove && <TakeOffTheNight name={dancer.name} onRemove={onRemove} />}
    </div>
      {/*
        Absent rather than zero when her household has gone
        and her date of birth with it (D-478). A dash reads as
        "not known"; 0 read as a newborn.
      */}
      <span className="tabular shrink-0 text-[12px] text-ink-3">
        {dancer.ageYears ?? '—'}
      </span>

      {/*
        <b>Who is expected tonight, not just who is enrolled</b>
        (D-476).

        Both only ever arrive from the occurrence door, which
        knows a night; the class door answers a question with
        no night in it and sends neither (A-1). So this is
        content-driven and does not reintroduce the
        which-door split D-466 removed.

        <b>"Out" is not "excused."</b> Telling the studio is
        courtesy, not an exemption — only Tonya excuses
        (D-188). The word says a family has spoken, and the
        reason stays on the attendance sheet where the
        capability for it lives.
      */}
      {dancer.reportedOut && (
        <span className="shrink-0 rounded-md bg-warn-bg px-1.5 py-0.5 text-[11px] font-bold text-warn">
          Out
        </span>
      )}

      {/*
        Her attendance in *this* class — the question a teacher
        looking at her own roster is asking. The household drops
        out to make room; it is a billing detail and is on the
        dancer's record (D-169).
      */}
      {/*
        <b>A visitor has no standing in this class</b> (D-479).

        Standings are keyed on enrolment, so a dancer dropping
        in to make up a missed session has no row here — and
        this fell through to her household name, in the column
        every other row uses for a percentage. A family name
        where a figure belongs is the kind of thing somebody
        acts on (D-209).

        Her standing in the class she actually missed was the
        other candidate and is worse: a real number, about a
        different class, read as this one's.
      */}
      {/*
        <b>The chip takes the standing column's place, it does not join it</b>
        (D-483).

        A row on a 390px phone already carries a name, an age and sometimes an
        `Out` badge; a fifth thing would leave the name about thirty
        characters. And the trade is the right way round: a teacher with the
        register open is asking <i>who is here</i>, not <i>what is her
        percentage</i> &mdash; the percentage is one tap away on her record,
        and it is a number about the season rather than about tonight.
      */}
      {visiting ? (
        <span className="w-28 shrink-0 truncate text-right text-[11.5px] font-semibold text-accent-2">
          Visiting
        </span>
      ) : standing ? (
        <ClassBadge
          attendance={standing.inThisClass}
          threshold={standing.overall.threshold}
          gated={standing.overall.isGated}
          size="small"
        />
      ) : (
        dancer.household && (
          <span className="w-28 shrink-0 truncate text-right text-[11.5px] text-ink-3">
            {dancer.household}
          </span>
        )
      )}
    </div>

    {mark && marking && (
      <MarkButtons
        dancerName={dancer.name}
        state={optimistic ?? mark.state}
        note={mark.note}
        familyNote={mark.familyNote}
        /*
          Two across, not four (D-128, D-634). The kiosk measured a four-column
          row at 320px giving each button about 70px, under the comfortable tap
          target - and this panel is a modal, so it is narrower than the tablet
          that measurement came from, never wider.
        */
        columns={2}
        busy={busy}
        locked={locked}
        onMark={marking}
      />
    )}
  </li>
  )
}
